<template>
  <div
    class="workflow-input-item group relative py-1 px-3 rounded-lg bg-surface-0 transition-all duration-200"
    :class="{
      'drag-over':
        dragOverInputId === input.id &&
        shouldAcceptFileDrop(input) &&
        !isEditMode
    }"
    @dragover.prevent="
      shouldAcceptFileDrop(input) && !isEditMode
        ? handleDragOver($event, input.id)
        : null
    "
    @dragleave.prevent="
      shouldAcceptFileDrop(input) && !isEditMode
        ? handleDragLeave($event)
        : null
    "
    @drop.prevent.stop="
      shouldAcceptFileDrop(input) && !isEditMode
        ? handleDrop($event, input)
        : null
    "
  >
    <!-- 删除按钮 - 只在编辑模式下显示 -->
    <div v-if="isEditMode" class="absolute top-2 right-2 z-10">
      <Button
        icon="pi pi-times"
        size="small"
        severity="danger"
        text
        rounded
        class="delete-button"
        @click="$emit('remove-input', input.id)"
      />
    </div>

    <div class="flex items-center justify-between mb-1">
      <div class="flex items-center gap-2">
        <!-- 编辑模式下显示输入框 -->
        <InputText
          v-if="isEditMode"
          v-model="localInput.name"
          class="text-base font-bold"
          size="small"
          @blur="handleSaveInputName"
          @keyup.enter="handleSaveInputName"
        />
        <!-- 非编辑模式下显示可点击标题 -->
        <div v-else class="flex items-center gap-1.5">
          <h4
            class="font-bold text-surface-900 text-base cursor-pointer hover:text-primary-600 transition-colors"
            @click="$emit('edit-input-name', input.id)"
          >
            {{ input.name }}
          </h4>
          <!-- 说明图标 -->
          <i
            v-if="input.description"
            v-tooltip.bottom="input.description"
            class="pi pi-question-circle text-sm text-surface-400 cursor-help hover:text-primary-500 transition-colors"
          ></i>
        </div>
      </div>
      <!-- 优化提示词按钮 - 放在标题右边 -->
      <div
        v-if="
          (input.type === 'customtext' ||
            input.type === 'text' ||
            input.type === 'string' ||
            input.type === 'multiline') &&
          input.config?.llmPrompt &&
          String(input.config.llmPrompt).trim() !== '' &&
          !isEditMode
        "
        class="flex items-center gap-2"
      >
        <!-- 如果已经优化过，显示撤回和重新生成按钮 -->
        <template v-if="optimizedInputIds?.has(input.id)">
          <Button
            icon="pi pi-undo"
            :label="$t('workflowApps.revertPrompt')"
            size="small"
            severity="secondary"
            :disabled="isOptimizing"
            text
            rounded
            class="text-xs"
            @click="$emit('revert-prompt', input.id)"
          />
          <Button
            :icon="
              isOptimizing && currentOptimizingInputId === input.id
                ? 'pi pi-spin pi-spinner'
                : 'pi pi-refresh'
            "
            :label="$t('workflowApps.regeneratePrompt')"
            size="small"
            severity="secondary"
            :disabled="
              isOptimizing || !inputValue || !String(inputValue || '').trim()
            "
            text
            rounded
            class="text-xs"
            @click="$emit('optimize-prompt', input.id, true)"
          />
        </template>
        <!-- 如果还没优化过，显示优化按钮 -->
        <Button
          v-else
          :icon="
            isOptimizing && currentOptimizingInputId === input.id
              ? 'pi pi-spin pi-spinner'
              : 'pi pi-magic'
          "
          :label="$t('workflowApps.optimizePrompt')"
          size="small"
          :disabled="
            isOptimizing || !inputValue || !String(inputValue || '').trim()
          "
          class="optimize-prompt-btn"
          @click="$emit('optimize-prompt', input.id)"
        />
      </div>
    </div>

    <div class="workflow-input-control mb-0.5">
      <!-- 音频选择+上传（预设选项 + 自定义上传） -->
      <template v-if="input.type === 'file.audio.select'">
        <Select
          :model-value="audioSelectDisplayValue"
          :options="audioSelectOptions"
          option-label="label"
          option-value="value"
          :placeholder="$t('workflowApps.selectAudio')"
          :disabled="isEditMode"
          class="w-full workflow-input-field mb-2"
          :class="{ 'opacity-50 cursor-not-allowed': isEditMode }"
          size="small"
          @update:model-value="handleAudioSelectChange"
        />
        <!-- 选择自定义时显示上传区域 -->
        <WorkflowFileUpload
          v-if="isAudioSelectCustomMode"
          :input="audioSelectFileInput"
          :input-value="inputValue"
          :file-metadata="fileMetadata"
          :upload-progress="uploadProgress || 0"
          :is-edit-mode="isEditMode"
          :image-url="imageUrl"
          :file-url="fileUrl"
          :hovered-upload-input-id="hoveredUploadInputId"
          :hovered-preview-input-id="hoveredPreviewInputId"
          @file-upload="(e) => $emit('file-upload', e, input)"
          @open-file-selector="$emit('open-file-selector', input)"
          @trigger-file-upload="$emit('trigger-file-upload', input)"
          @clear-file-value="$emit('clear-file-value', input.id)"
          @open-image-viewer="$emit('open-image-viewer', input.id)"
          @open-crop-existing-image="
            $emit('open-crop-existing-image', input.id)
          "
          @open-trim-existing-audio="
            $emit('open-trim-existing-audio', input.id)
          "
          @open-trim-existing-video="
            $emit('open-trim-existing-video', input.id)
          "
          @paste-image="(file) => $emit('paste-image', file, input)"
          @update:hovered-upload-input-id="
            (val) => $emit('update:hovered-upload-input-id', val)
          "
          @update:hovered-preview-input-id="
            (val) => $emit('update:hovered-preview-input-id', val)
          "
        />
      </template>

      <!-- 文件选择功能（图片/音频） -->
      <WorkflowFileUpload
        v-else-if="shouldShowFileSelector(input)"
        :input="input"
        :input-value="inputValue"
        :file-metadata="fileMetadata"
        :upload-progress="uploadProgress || 0"
        :is-edit-mode="isEditMode"
        :image-url="imageUrl"
        :file-url="fileUrl"
        :hovered-upload-input-id="hoveredUploadInputId"
        :hovered-preview-input-id="hoveredPreviewInputId"
        @file-upload="(e) => $emit('file-upload', e, input)"
        @open-file-selector="$emit('open-file-selector', input)"
        @trigger-file-upload="$emit('trigger-file-upload', input)"
        @clear-file-value="$emit('clear-file-value', input.id)"
        @open-image-viewer="$emit('open-image-viewer', input.id)"
        @open-crop-existing-image="$emit('open-crop-existing-image', input.id)"
        @open-trim-existing-audio="$emit('open-trim-existing-audio', input.id)"
        @open-trim-existing-video="$emit('open-trim-existing-video', input.id)"
        @paste-image="(file) => $emit('paste-image', file, input)"
        @update:hovered-upload-input-id="
          (val) => $emit('update:hovered-upload-input-id', val)
        "
        @update:hovered-preview-input-id="
          (val) => $emit('update:hovered-preview-input-id', val)
        "
      />

      <!-- 普通输入组件 -->
      <component
        :is="getInputComponent(input.type)"
        v-if="
          getInputComponent(input.type) && input.type !== 'file.audio.select'
        "
        :model-value="inputValue"
        :placeholder="input.placeholder || $t('workflowApps.enterValue')"
        :options="input.options"
        :min="input.config?.min"
        :max="input.config?.max"
        :step="input.config?.step"
        :maxlength="input.config?.maxLength"
        :disabled="isEditMode"
        :auto-resize="
          input.type === 'text' ||
          input.type === 'string' ||
          input.type === 'customtext' ||
          input.type === 'multiline'
        "
        :rows="1"
        class="w-full workflow-input-field"
        :class="{ 'opacity-50 cursor-not-allowed': isEditMode }"
        size="small"
        option-label="label"
        option-value="value"
        @update:model-value="
          (value: any) => $emit('input-change', input.id, value)
        "
        @click="
          input.type === 'combo' ? $emit('combo-click', input.id, $event) : null
        "
      />
    </div>

    <div
      v-if="input.description && isEditMode"
      class="text-xs text-surface-600 leading-relaxed mt-1"
    >
      {{ input.description }}
    </div>

    <!-- 编辑模式下的配置编辑器 -->
    <InputConfigEditor
      :input="input"
      :is-edit-mode="isEditMode"
      :input-type-options="inputTypeOptions"
      :can-move-up="canMoveUp"
      :can-move-down="canMoveDown"
      :preset-uploading-for="presetUploadingFor"
      @update-config="
        (prop, val) => $emit('update-input-config', input.id, prop, val)
      "
      @update-input-id="
        (newId, nodeId, widgetName) =>
          $emit('update-input-id', input.id, newId, nodeId, widgetName)
      "
      @move-up="$emit('move-input-up', input.id)"
      @move-down="$emit('move-input-down', input.id)"
      @add-combo-option="$emit('add-combo-option', input.id)"
      @update-combo-option="
        (index, prop, val) =>
          $emit('update-combo-option', input.id, index, prop, val)
      "
      @remove-combo-option="
        (index) => $emit('remove-combo-option', input.id, index)
      "
      @upload-audio-for-option="
        (optionIndex, file) =>
          $emit('upload-audio-for-option', input.id, optionIndex, file)
      "
      @upload-audio-for-default-value="
        (file) => $emit('upload-audio-for-default-value', input.id, file)
      "
    />
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import ToggleSwitch from 'primevue/toggleswitch'
import { computed, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import InputConfigEditor from './InputConfigEditor.vue'
import WorkflowFileUpload from './WorkflowFileUpload.vue'

interface WorkflowInput {
  id: string
  name: string
  type: string
  value: any
  description?: string
  placeholder?: string
  options?: any[]
  order?: number
  nodeId?: string
  widgetName?: string
  config: {
    min?: number
    max?: number
    step?: number
    defaultValue?: any
    required?: boolean
    order?: number
    llmPrompt?: string
    [key: string]: any
  }
}

type FileMetadataItem = {
  name?: string
  obsUrl?: string
  originalFilename?: string
  fileId?: number
  previewUrl?: string
  isUploading?: boolean
}

const props = withDefaults(
  defineProps<{
    input: WorkflowInput
    presetUploadingFor?: {
      inputId: string
      optionIndex?: number
      type?: 'default'
    } | null
    inputValue?: any
    fileMetadata?: FileMetadataItem
    uploadProgress?: number
    isEditMode?: boolean
    dragOverInputId?: string
    hoveredUploadInputId?: string | null
    hoveredPreviewInputId?: string | null
    imageUrl?: string
    fileUrl?: string
    inputTypeOptions?: Array<{ label: string; value: string }>
    optimizedInputIds?: Set<string>
    isOptimizing?: boolean
    currentOptimizingInputId?: string
    canMoveUp?: boolean
    canMoveDown?: boolean
  }>(),
  {
    presetUploadingFor: null,
    inputValue: '',
    uploadProgress: 0,
    isEditMode: false,
    dragOverInputId: '',
    hoveredUploadInputId: null,
    hoveredPreviewInputId: null,
    imageUrl: '',
    fileUrl: '',
    inputTypeOptions: () => [],
    optimizedInputIds: () => new Set(),
    isOptimizing: false,
    currentOptimizingInputId: '',
    canMoveUp: false,
    canMoveDown: false
  }
)

const { t } = useI18n()

const emit = defineEmits<{
  'remove-input': [inputId: string]
  'save-input-name': [inputId: string, name?: string]
  'edit-input-name': [inputId: string]
  'input-change': [inputId: string, value: any]
  'file-upload': [event: Event, input: WorkflowInput]
  'open-file-selector': [input: WorkflowInput]
  'trigger-file-upload': [input: WorkflowInput]
  'clear-file-value': [inputId: string]
  'open-image-viewer': [inputId: string]
  'open-crop-existing-image': [inputId: string]
  'open-trim-existing-audio': [inputId: string]
  'open-trim-existing-video': [inputId: string]
  'paste-image': [file: File, input: WorkflowInput]
  'optimize-prompt': [inputId: string, isRegenerate?: boolean]
  'revert-prompt': [inputId: string]
  'combo-click': [inputId: string, event: Event]
  'update-input-config': [inputId: string, property: string, value: any]
  'update-input-id': [
    oldInputId: string,
    newInputId: string,
    nodeId: string,
    widgetName: string
  ]
  'move-input-up': [inputId: string]
  'move-input-down': [inputId: string]
  'add-combo-option': [inputId: string]
  'update-combo-option': [
    inputId: string,
    index: number,
    property: 'label' | 'value',
    value: string
  ]
  'remove-combo-option': [inputId: string, index: number]
  'upload-audio-for-option': [inputId: string, optionIndex: number, file: File]
  'upload-audio-for-default-value': [inputId: string, file: File]
  'handle-drag-over': [event: DragEvent, inputId: string]
  'handle-drag-leave': [event: DragEvent]
  'handle-drop': [event: DragEvent, input: WorkflowInput]
  'update:hovered-upload-input-id': [value: string | null]
  'update:hovered-preview-input-id': [value: string | null]
}>()

// 创建本地响应式副本用于编辑名称
const localInput = reactive<WorkflowInput>(
  JSON.parse(JSON.stringify(props.input))
)

watch(
  () => props.input,
  (newInput) => {
    Object.assign(localInput, JSON.parse(JSON.stringify(newInput)))
  },
  { deep: true }
)

const AUDIO_SELECT_CUSTOM_VALUE = '__custom__'

const shouldShowFileSelector = (input: WorkflowInput): boolean => {
  return (
    input.type === 'file.image' ||
    input.type === 'file.audio' ||
    input.type === 'file.video'
  )
}

// 音频选择+上传：选项列表（预设 + 自定义）
const audioSelectOptions = computed(() => {
  if (props.input.type !== 'file.audio.select') return []
  const presetOptions = (props.input.options || []).filter(
    (o: { value?: string }) => o?.value !== AUDIO_SELECT_CUSTOM_VALUE
  ) as Array<{ label: string; value: string }>
  return [
    ...presetOptions,
    {
      label: props.input.config?.customLabel ?? t('workflowApps.custom'),
      value: AUDIO_SELECT_CUSTOM_VALUE
    }
  ]
})

// 音频选择：当前 Select 显示值（预设 value 或 __custom__）
const audioSelectDisplayValue = computed(() => {
  if (props.input.type !== 'file.audio.select') return ''
  const val = props.inputValue
  if (!val || val === AUDIO_SELECT_CUSTOM_VALUE)
    return AUDIO_SELECT_CUSTOM_VALUE
  const preset = audioSelectOptions.value.find((o) => o.value === val)
  return preset ? val : AUDIO_SELECT_CUSTOM_VALUE
})

// 是否为自定义模式（显示上传框）
const isAudioSelectCustomMode = computed(() => {
  return (
    props.input.type === 'file.audio.select' &&
    audioSelectDisplayValue.value === AUDIO_SELECT_CUSTOM_VALUE
  )
})

// 传给 WorkflowFileUpload 的 input（type 改为 file.audio）
const audioSelectFileInput = computed(() => ({
  ...props.input,
  type: 'file.audio' as const
}))

const handleAudioSelectChange = (value: string) => {
  emit('input-change', props.input.id, value)
}

// 是否接受文件拖放（含 file.audio.select 的自定义模式）
const shouldAcceptFileDrop = (input: WorkflowInput): boolean => {
  if (input.type === 'file.audio.select') {
    return isAudioSelectCustomMode.value
  }
  return shouldShowFileSelector(input)
}

const getInputComponent = (type: string) => {
  switch (type) {
    case 'string':
    case 'text':
    case 'customtext':
    case 'multiline':
      return Textarea
    case 'number':
    case 'int':
    case 'float':
      return InputNumber
    case 'boolean':
      return ToggleSwitch
    case 'combo':
      return Select
    case 'file.image':
    case 'file.audio':
    case 'file.video':
      return null
    default:
      return InputText
  }
}

const handleSaveInputName = () => {
  const newName = localInput.name.trim()
  console.log('[WorkflowInputItem] handleSaveInputName:', {
    inputId: props.input.id,
    newName,
    localInputName: localInput.name
  })
  if (newName) {
    // 先通过 update-input-config 更新名称，确保父组件能够获取到最新的名称
    emit('update-input-config', props.input.id, 'name', newName)
    // 然后触发 save-input-name 事件，并传递新的名称值
    emit('save-input-name', props.input.id, newName)
    console.log('[WorkflowInputItem] 已发送 save-input-name 事件:', {
      inputId: props.input.id,
      newName
    })
  }
}

const handleDragOver = (event: DragEvent, inputId: string) => {
  emit('handle-drag-over', event, inputId)
}

const handleDragLeave = (event: DragEvent) => {
  emit('handle-drag-leave', event)
}

const handleDrop = (event: DragEvent, input: WorkflowInput) => {
  emit('handle-drop', event, input)
}
</script>

<style scoped>
/* 使用项目现有的CSS变量系统 */
.workflow-input-item {
  background-color: var(--comfy-menu-secondary-bg);
  color: var(--p-text-color, #fff);
  transition: all 0.2s ease;
}

.workflow-input-item:hover {
  border-color: var(--p-primary-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 强制覆盖Tailwind类名，使用项目变量 */
.workflow-input-item.bg-surface-0 {
  background-color: var(--comfy-menu-secondary-bg) !important;
}

.workflow-input-item h4 {
  color: var(--p-text-color, #fff);
  margin-top: 0;
  margin-bottom: 0.125rem;
}

.workflow-input-item .text-xs {
  color: var(--p-text-muted-color, #9ca3af);
}

.workflow-input-item.drag-over {
  border-color: var(--p-primary-color) !important;
  background-color: rgba(
    var(--p-primary-color-rgb, 102, 126, 234),
    0.1
  ) !important;
  transform: scale(1.01);
  box-shadow: 0 4px 12px rgba(var(--p-primary-color-rgb, 102, 126, 234), 0.2) !important;
}

.delete-button {
  padding: 4px !important;
  min-width: 24px !important;
  height: 24px !important;
  border-radius: 4px !important;
}

.delete-button:hover {
  background-color: var(--p-red-100) !important;
  border-radius: 4px !important;
}

.workflow-input-control :deep(.p-inputtext),
.workflow-input-control :deep(.p-inputnumber),
.workflow-input-control :deep(.p-select),
.workflow-input-control :deep(.p-textarea) {
  width: 100%;
}

.workflow-input-item .file-preview-wrapper:has(.image-preview),
.workflow-input-item .file-preview-wrapper:has(.file-upload-placeholder),
.workflow-input-item .file-preview-wrapper:has(.uploading-overlay) {
  max-width: 200px;
}

/* 图片预览最大宽度 */
.workflow-input-item .thumbnail-image {
  max-width: 200px;
}

/* 编辑模式下缩小预览大小 */
.workflow-input-item .file-preview-wrapper.edit-mode {
  max-width: 120px !important;
  width: 120px !important;
}
</style>
